import { AsyncState, Fun, Updater, loadedAsyncState } from 'widgets-for-react'
import { getBlogTeasers } from '../../blogs/blogOverview/blogOverviewApi'
import {
  BlogTeasersState,
  initialBlogTeasersState,
} from '../../blogs/blogTeasers/blogTeasersState'
import {
  CollectionEditorState,
  collectionEditorStateUpdaters,
  initialCollectionEditorState,
} from '../../collections/collectionEditor/collectionEditorState'
import { getComponentTeasers } from '../../components/componentOverview/componentOverviewApi'
import {
  ComponentSaverState,
  componentSaverStateUpdaters,
  initialComponentSaverState,
} from '../../components/componentSaver/componentSaverState'
import {
  ComponentTeaserData,
  ComponentTeasersState,
  ExtendedComponentTeasersState,
  initialComponentTeasersState,
} from '../../components/componentTeasers/componentTeasersState'
import { HomeData } from '../home.state'
import {
  getLastViewedComponents,
  loadAdditionalSubsections,
  loadComponentOfTheDay,
} from './loggedInHomeApi'

export type ExtendedLoggedInHomeState = LoggedInHomeState & {
  reloadCollections?: boolean
  searchString?: string
  homeDataUpdater?: Updater<HomeData>
}

export type LoggedInHomeState = {
  newestComponents: ComponentTeasersState
  lastViewedComponents: ComponentTeasersState
  additionalSubsections: AsyncState<AdditionalSubsectionState[]>
  componentSaverState: ComponentSaverState
  componentOfTheDay: AsyncState<ComponentTeaserData | undefined>
  collectionCreatorState: CollectionEditorState
  blogTeasers: BlogTeasersState
}

export type AdditionalSubsectionState = AdditionalSubsectionData & {
  matchingComponents: ComponentTeasersState
}

export type AdditionalSubsectionData = {
  Id: number
  Name: string
  Query: string
  Description: string
}

export const initialLoggedInHomeState: LoggedInHomeState = {
  newestComponents: initialComponentTeasersState(getComponentTeasers),
  lastViewedComponents: initialComponentTeasersState(getLastViewedComponents),
  additionalSubsections: loadAdditionalSubsections,
  componentSaverState: initialComponentSaverState,
  componentOfTheDay: loadComponentOfTheDay,
  collectionCreatorState: initialCollectionEditorState,
  blogTeasers: initialBlogTeasersState(() => getBlogTeasers(1, 2)),
}

export const loggedInHomeStateUpdaters = {
  newestComponentsUpdater:
    (update: Fun<ComponentTeasersState, ExtendedComponentTeasersState>) =>
    (state: LoggedInHomeState): LoggedInHomeState => {
      const { saveComponentId, ...newState } = update(state.newestComponents)
      return {
        ...state,
        newestComponents: newState,
        componentSaverState:
          saveComponentId == 'none'
            ? state.componentSaverState
            : componentSaverStateUpdaters.startSavingUpdater(saveComponentId)(
                state.componentSaverState
              ),
      }
    },
  lastViewedComponentsUpdater:
    (update: Fun<ComponentTeasersState, ExtendedComponentTeasersState>) =>
    (state: LoggedInHomeState): LoggedInHomeState => {
      const { saveComponentId, ...newState } = update(
        state.lastViewedComponents
      )
      return {
        ...state,
        lastViewedComponents: newState,
        componentSaverState:
          saveComponentId == 'none'
            ? state.componentSaverState
            : componentSaverStateUpdaters.startSavingUpdater(saveComponentId)(
                state.componentSaverState
              ),
      }
    },
  matchingComponentsUpdater:
    (sectionId: number) =>
    (update: Fun<ComponentTeasersState, ExtendedComponentTeasersState>) =>
    (state: LoggedInHomeState): LoggedInHomeState => {
      if (state.additionalSubsections.kind == 'loaded') {
        const sectionTobeUpdated = state.additionalSubsections.value.find(
          (section) => section.Id == sectionId
        )
        if (sectionTobeUpdated != undefined) {
          const { saveComponentId, ...newState } = update(
            sectionTobeUpdated.matchingComponents
          )
          return {
            ...state,
            additionalSubsections: loadedAsyncState(
              state.additionalSubsections.value.map((section) =>
                section.Id == sectionId
                  ? {
                      ...section,
                      matchingComponents: newState,
                    }
                  : section
              )
            ),
            componentSaverState:
              saveComponentId == 'none'
                ? state.componentSaverState
                : componentSaverStateUpdaters.startSavingUpdater(
                    saveComponentId
                  )(state.componentSaverState),
          }
        } else return state
      } else return state
    },
  componentSaverStateUpdater:
    (
      update: Fun<
        ComponentSaverState,
        ComponentSaverState & { reloadCollections?: boolean }
      >
    ) =>
    (
      state: LoggedInHomeState
    ): LoggedInHomeState & { reloadCollections?: boolean } => {
      const { reloadCollections, ...newState } = update(
        state.componentSaverState
      )
      return {
        ...state,
        componentSaverState: newState,
        reloadCollections: reloadCollections,
      }
    },
  startCreatingUpdater: (state: LoggedInHomeState): LoggedInHomeState => ({
    ...state,
    collectionCreatorState: collectionEditorStateUpdaters.startEditingUpdater(
      '',
      ''
    )(state.collectionCreatorState),
  }),
  collectionCreatorStateUpdater:
    (
      updater: Fun<
        CollectionEditorState,
        CollectionEditorState & { reloadCollections?: boolean }
      >
    ) =>
    (
      state: LoggedInHomeState
    ): LoggedInHomeState & { reloadCollections?: boolean } => {
      const { reloadCollections, ...newState } = updater(
        state.collectionCreatorState
      )
      return {
        ...state,
        collectionCreatorState: newState,
        reloadCollections: reloadCollections,
      }
    },
}
